'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  INTERSTITIAL_EXIT_DURATION_MS,
  interstitialTiming,
  type InterstitialTiming,
} from '../motion'
import { useSequenceStart } from './useSequenceStart'

// Ceiling: past this the overlay leaves whether or not the results arrived, so a
// stalled upstream can never strand the user behind it. A sequence set longer
// than this (`?loaderMs=`) raises it, so the sequence is never cut off.
const MAX_DISPLAY = 6000
// Derived from the exit choreography (motion.ts) with a frame of headroom, so the
// overlay is never lifted until the last orbiting card has fully left the screen —
// the page can't be revealed mid-animation.
const EXIT_DURATION = INTERSTITIAL_EXIT_DURATION_MS
// The overlay keeps swallowing taps for a beat after the exit starts, so a tap can't land
// on the header while the overlay is still visually present (an invisible, clickable button).
const POINTER_RELEASE_DELAY = 100
const SEEN_KEY_PREFIX = 'mtag:interstitial-seen:'

type SearchInterstitialState = {
  blocksPointerEvents: boolean
  isExiting: boolean
  onPhrasesComplete: () => void
  showInterstitial: boolean
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// True the first time `onceKey` is claimed in this browser session. Blocked
// storage (private mode, disabled site data) falls back to always showing.
function claimFirstShow(onceKey: string): boolean {
  try {
    const key = SEEN_KEY_PREFIX + onceKey

    if (window.sessionStorage.getItem(key)) {
      return false
    }

    window.sessionStorage.setItem(key, '1')

    return true
  } catch {
    return true
  }
}

// The floor is the whole loading sequence (`timing.durationMs`, see motion.ts):
// long enough for every phrase to be seen and the orbit to fill. Leaving earlier
// cuts the story off at the first phrase. Every millisecond above it belongs to
// the data.
//
// With `onceKey`, the overlay plays only on the first visit per key in a browser
// session; later visits skip straight to the page and its own loading state.
export function useSearchInterstitial(
  dataLoaded: boolean,
  timing: InterstitialTiming = interstitialTiming(),
  onceKey?: string,
): SearchInterstitialState {
  const floor = timing.durationMs
  const ceiling = Math.max(MAX_DISPLAY, floor)
  // Shown even when the PDP prefetch already has the results in hand: the overlay
  // is part of the discovery experience, not only a loading state. A warm arrival
  // still leaves at the floor rather than on a wait.
  const [showInterstitial, setShowInterstitial] = useState(true)
  const [isExiting, setIsExiting] = useState(false)
  const [blocksPointerEvents, setBlocksPointerEvents] = useState(true)
  // Counted from first paint on a document load (useSequenceStart), so the time
  // spent waiting on hydration behind the server-rendered overlay counts too.
  const { startedAt } = useSequenceStart()
  const exitStarted = useRef(false)
  // The exit timers outlive the render that scheduled them, and the user can leave
  // mid-interstitial (that is what the back button does), so they have to be
  // cancellable from an unmount cleanup rather than left to fire into the void.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  // Decided once per mount: Strict Mode re-runs effects, and a second claim would
  // find the key the first one just set and hide the overlay mid-sequence.
  const firstShowDecided = useRef(false)

  const schedule = useCallback((callback: () => void, delay: number) => {
    timers.current.push(setTimeout(callback, delay))
  }, [])

  useEffect(() => {
    const pending = timers.current

    return () => {
      for (const timer of pending) {
        clearTimeout(timer)
      }

      pending.length = 0
    }
  }, [])

  // A layout effect, so a repeat visit never paints the overlay before hiding it.
  useLayoutEffect(() => {
    if (!onceKey || firstShowDecided.current) {
      return
    }

    firstShowDecided.current = true

    if (claimFirstShow(onceKey)) {
      return
    }

    exitStarted.current = true
    setShowInterstitial(false)
    setBlocksPointerEvents(false)
  }, [onceKey])

  const startExit = useCallback(() => {
    if (exitStarted.current) {
      return
    }

    exitStarted.current = true
    setIsExiting(true)

    const reducedMotion = prefersReducedMotion()
    const exitDuration = reducedMotion ? 0 : EXIT_DURATION
    const pointerRelease = reducedMotion ? 0 : POINTER_RELEASE_DELAY

    schedule(() => setBlocksPointerEvents(false), pointerRelease)
    schedule(() => setShowInterstitial(false), exitDuration)
  }, [schedule])

  // Idempotent by way of `startExit`, so it is safe to call from every path that
  // might have changed the answer.
  const tryExit = useCallback(() => {
    if (exitStarted.current) {
      return
    }

    const elapsed = Date.now() - startedAt

    if (elapsed >= ceiling) {
      startExit()

      return
    }

    if (!dataLoaded) {
      return
    }

    const minDisplay = prefersReducedMotion() ? 0 : floor

    if (elapsed >= minDisplay) {
      startExit()

      return
    }

    schedule(startExit, minDisplay - elapsed)
  }, [dataLoaded, startExit, schedule, ceiling, floor, startedAt])

  // Phrases used to gate the exit, which meant a result that landed in under a
  // second still sat behind the full sequence. They are decoration now: this hook
  // re-evaluates whenever `dataLoaded` changes, and the callback only nudges it.
  const onPhrasesComplete = useCallback(() => {
    tryExit()
  }, [tryExit])

  useEffect(() => {
    if (exitStarted.current) {
      return undefined
    }

    tryExit()

    // Nothing re-renders this hook if the results never settle, so the ceiling
    // needs a timer of its own.
    const remaining = Math.max(0, ceiling - (Date.now() - startedAt))
    const maxTimer = setTimeout(tryExit, remaining)

    return () => clearTimeout(maxTimer)
  }, [tryExit, ceiling, startedAt])

  return { showInterstitial, isExiting, onPhrasesComplete, blocksPointerEvents }
}
